import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'
import { NavigationItem } from '../types'
import { useAuthStore } from '../stores/authStore'

interface LayoutProps {
  children: React.ReactNode
  toggleTheme?: () => void
  isDarkMode?: boolean
}

const Layout = ({ children, toggleTheme, isDarkMode }: LayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()

  // Check if current route is login or signup page
  const isAuthPage =
    location.pathname === '/login' || location.pathname === '/signup'

  // Generate role-based navigation
  const getRoleBasedNavigation = (): NavigationItem[][] => {
    const { getUserRole, getUserPermissions } = useAuthStore.getState()
    const userRole = getUserRole()
    const permissions = getUserPermissions()

    const mainNavigation: NavigationItem[] = []

    // Dashboard - available to all roles
    mainNavigation.push({
      label: 'Dashboard',
      icon: 'dashboard',
      to: '/',
      onSelect: () => setSidebarOpen(false),
    })

    // Role-specific navigation
    switch (userRole) {
      case 'ADMIN':
        mainNavigation.push(
          {
            label: 'Tenants',
            icon: 'tenants',
            to: '/tenants',
            onSelect: () => setSidebarOpen(false),
          },
          {
            label: 'Users',
            icon: 'users',
            to: '/settings/users',
            onSelect: () => setSidebarOpen(false),
          },
          {
            label: 'Projects',
            icon: 'projects',
            to: '/projects',
            onSelect: () => setSidebarOpen(false),
          },
          {
            label: 'Assets',
            icon: 'assets',
            to: '/assets',
            onSelect: () => setSidebarOpen(false),
          },
          {
            label: 'Roles',
            icon: 'roles',
            to: '/roles',
            onSelect: () => setSidebarOpen(false),
          },
          {
            label: 'Permissions',
            icon: 'permissions',
            to: '/permissions',
            onSelect: () => setSidebarOpen(false),
          },
          {
            label: 'Activity Logs',
            icon: 'activity',
            to: '/activity-logs',
            onSelect: () => setSidebarOpen(false),
          },
          {
            label: 'Blogs',
            icon: 'blogs',
            to: '/blogs',
            onSelect: () => setSidebarOpen(false),
          }
        )
        break

      case 'TENANT_ADMIN':
        mainNavigation.push(
          {
            label: 'Users',
            icon: 'users',
            to: '/settings/users',
            onSelect: () => setSidebarOpen(false),
          },
          {
            label: 'Projects',
            icon: 'projects',
            to: '/projects',
            onSelect: () => setSidebarOpen(false),
          }
        )
        break

      case 'TENANT_USER':
        mainNavigation.push({
          label: 'Projects',
          icon: 'projects',
          to: '/projects',
          onSelect: () => setSidebarOpen(false),
        })
        break

      case 'PENTESTER':
        mainNavigation.push({
          label: 'Projects',
          icon: 'projects',
          to: '/projects',
          onSelect: () => setSidebarOpen(false),
        })
        break
    }

    // Settings - available to ADMIN and TENANT_ADMIN
    if (permissions.canManageSettings) {
      mainNavigation.push({
        label: 'Settings',
        to: '/settings/security',
        icon: 'settings',
        defaultOpen: false,
        type: 'trigger',
        children: [
          ...(permissions.canManageUsers
            ? [
              {
                label: 'Users',
                icon: 'userManagement',
                to: '/settings/users',
                onSelect: () => setSidebarOpen(false),
              },
            ]
            : []),
          {
            label: 'Security',
            icon: 'security',
            to: '/settings/security',
            onSelect: () => setSidebarOpen(false),
          },
        ],
      })
    }

    return [mainNavigation]
  }

  const navigation = getRoleBasedNavigation()

  // If it's an auth page, render without sidebar and header
  if (isAuthPage) {
    return <>{children}</>
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar
        navigation={navigation}
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        currentPath={location.pathname}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          onMenuClick={() => setSidebarOpen(true)}
          toggleTheme={toggleTheme || (() => { })}
          isDarkMode={isDarkMode || false}
        />

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-6 py-8">{children}</div>
        </main>
      </div>
    </div>
  )
}

export default Layout
